import { useCallback, useState } from "react";
import { media } from "shared/api";

/**
 * Thin adapter around the `media` namespace for the Product Editor image
 * slots (Thumbnail + 1st/2nd/3rd Preview).
 *
 * Per `docs/endpoint-requirements.md` §5 the admin uploads through
 * `POST /api/v1/media`, routed through the shared `shared/api/media`
 * namespace (no inline fetch).
 *
 * @returns {{
 *   upload: (file: File) => Promise<{ id: string, url: string }>,
 *   isUploading: boolean,
 *   error: Error | null,
 *   reset: () => void,
 * }}
 */
export function useProductImageUploader() {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);

  const upload = useCallback(async (file) => {
    if (!file) {
      throw new Error("useProductImageUploader.upload: file is required");
    }
    setIsUploading(true);
    setError(null);
    try {
      const asset = await media.upload(file);
      return asset;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsUploading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setError(null);
    setIsUploading(false);
  }, []);

  return { upload, isUploading, error, reset };
}

export default useProductImageUploader;
